/**
 * Opening line for text chat: same source of truth as voice (greetingTemplate + {{variables}}).
 * Variable names match ScriptManager / SalesTwilio interpolateScriptVariables (case-insensitive aliases).
 */

const VAR_ALIASES = {
  firstname: "firstName",
  lastname: "lastName",
  businessname: "businessName",
  ainame: "aiName",
  scriptname: "scriptName",
};

function normalizeKey(key) {
  const k = String(key).trim();
  const lower = k.toLowerCase();
  return VAR_ALIASES[lower] || k;
}

export function interpolateTemplateVariables(text, context = {}) {
  if (text == null || typeof text !== "string") return text;
  return text.replace(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g, (_, key) => {
    const nk = normalizeKey(key);
    const v = context[nk];
    return v != null && String(v).trim() !== "" ? String(v) : "";
  });
}

export function greetingContextFromScript(scriptData) {
  if (!scriptData || typeof scriptData !== "object") {
    return { firstName: "", lastName: "", businessName: "", aiName: "", scriptName: "" };
  }
  const pick = (k) => {
    const v = scriptData[k];
    return v != null && String(v).trim() !== "" ? String(v).trim() : "";
  };
  const scriptName = pick("scriptName");
  const business = pick("businessName") || scriptName;
  return {
    firstName: pick("firstName"),
    lastName: pick("lastName"),
    businessName: business,
    aiName: pick("aiName"),
    scriptName,
  };
}

const DEFAULT_AI = "Maya";

/**
 * @param {object|null|undefined} scriptData — full script doc from GET /api/script (includes greetingTemplate, aiName, …)
 * @returns {string} first assistant line shown in the chat UI
 */
export function resolveChatbotGreeting(scriptData) {
  if (!scriptData) {
    return `Hi there! This is ${DEFAULT_AI} from our office. How can I help today?`;
  }
  const ctx = greetingContextFromScript(scriptData);
  const tpl = scriptData.greetingTemplate != null && String(scriptData.greetingTemplate).trim();
  if (tpl) {
    const line = interpolateTemplateVariables(tpl, ctx).trim();
    if (line) return line;
  }
  const aiName = ctx.aiName || DEFAULT_AI;
  const businessName = ctx.businessName || "our office";
  return `Hi there! This is ${aiName} from ${businessName}. How can I help today?`;
}

/**
 * Prefix assistant replies with the same configurable greeting used in the UI opener and voice.
 * Skips adding a second copy if the model already began with the resolved greeting.
 *
 * @param {object|null|undefined} scriptData
 * @param {string|null|undefined} replyText
 * @returns {string}
 */
export function formatAssistantReplyWithGreeting(scriptData, replyText) {
  const raw = replyText == null ? "" : String(replyText);
  const prefix = resolveChatbotGreeting(scriptData);
  const t = raw.trim();
  if (!t) return prefix;
  const p = prefix.trim();
  if (t.startsWith(p)) return raw;
  return `${prefix}\n\n${raw}`;
}
